import type { LanguageOption } from './types';

// Config constants
export const DEFAULT_LANGUAGE_CONFIG_KEY = 'default_language';
export const CONFIG_TYPE_LANGUAGE = 'language';

type SupportedLocale = {
  key: string;
  code: string;
  label: string;
};

// V1 questionnaire translations locale list (canonical order/source)
const V1_SUPPORTED_LOCALES: SupportedLocale[] = [
  { key: 'arabic', code: 'ar', label: 'العربية (Arabic)' },
  { key: 'armenian', code: 'hy', label: 'հայերեն (Armenian)' },
  { key: 'bengali', code: 'bn', label: 'বাংলা (Bengali)' },
  { key: 'chinese', code: 'zh', label: '中文 (Chinese)' },
  { key: 'croatian', code: 'hr', label: 'hrvatski (Croatian)' },
  { key: 'english', code: 'en', label: 'English' },
  { key: 'french', code: 'fr', label: 'Français (French)' },
  { key: 'german', code: 'de', label: 'Deutsch (German)' },
  { key: 'greek', code: 'el', label: 'ελληνικά (Greek)' },
  { key: 'gujarati', code: 'gu', label: 'ગુજરાતી (Gujarati)' },
  { key: 'haitian_creole', code: 'ht', label: 'Kreyòl Ayisyen (Haitian Creole)' },
  { key: 'hebrew', code: 'he', label: 'עברית (Hebrew)' },
  { key: 'hindi', code: 'hi', label: 'हिन्दी (Hindi)' },
  { key: 'hmong', code: 'hmn', label: 'Hmong' },
  { key: 'italian', code: 'it', label: 'Italiano (Italian)' },
  { key: 'japanese', code: 'ja', label: '日本語 (Japanese)' },
  { key: 'khmer', code: 'km', label: 'ភាសាខ្មែរ (Khmer)' },
  { key: 'korean', code: 'ko', label: '한국어 (Korean)' },
  { key: 'persian', code: 'fa', label: 'فارسی (Persian)' },
  { key: 'polish', code: 'pl', label: 'Polski (Polish)' },
  { key: 'portuguese', code: 'pt', label: 'Português (Portuguese)' },
  { key: 'punjabi', code: 'pa', label: 'ਪੰਜਾਬੀ (Punjabi)' },
  { key: 'russian', code: 'ru', label: 'Русский (Russian)' },
  { key: 'spanish', code: 'es', label: 'Español (Spanish)' },
  { key: 'tagalog', code: 'tl', label: 'Tagalog' },
  { key: 'tamil', code: 'ta', label: 'தமிழ் (Tamil)' },
  { key: 'telugu', code: 'te', label: 'తెలుగు (Telugu)' },
  { key: 'thai', code: 'th', label: 'ไทย (Thai)' },
  { key: 'urdu', code: 'ur', label: 'اردو (Urdu)' },
  { key: 'vietnamese', code: 'vi', label: 'Tiếng Việt (Vietnamese)' },
  { key: 'yiddish', code: 'yi', label: 'ייִדיש (Yiddish)' },
];

const LOCALE_BY_KEY = new Map(V1_SUPPORTED_LOCALES.map((locale) => [locale.key, locale]));
const LOCALE_KEY_BY_CODE: Record<string, string> = Object.fromEntries(
  V1_SUPPORTED_LOCALES.map((locale) => [locale.code, locale.key]),
);

const LEGACY_ALIAS_TO_KEY: Record<string, string> = {
  en_us: 'english',
  en_gb: 'english',
  pt_br: 'portuguese',
  pt_pt: 'portuguese',
  kr: 'korean',
  haitiancreole: 'haitian_creole',
};

const normalizeLocaleInput = (value: string): string => {
  return value.trim().toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
};

export const normalizeLocaleKey = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;

  const normalized = normalizeLocaleInput(value);
  if (!normalized) return null;

  const aliased = LEGACY_ALIAS_TO_KEY[normalized] || normalized;
  if (LOCALE_BY_KEY.has(aliased)) {
    return aliased;
  }

  return LOCALE_KEY_BY_CODE[aliased] || null;
};

export const getLocaleKey = (value?: string | null, fallback = 'english'): string => {
  return normalizeLocaleKey(value) || fallback;
};

export const getLocaleCode = (value?: string | null, fallback = 'en'): string => {
  const localeKey = normalizeLocaleKey(value);
  if (!localeKey) return fallback;
  return LOCALE_BY_KEY.get(localeKey)?.code || fallback;
};

export const normalizeLocaleKeys = (values?: string[] | null): string[] => {
  if (!Array.isArray(values)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  values.forEach((value) => {
    const key = normalizeLocaleKey(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    normalized.push(key);
  });

  return normalized;
};

export interface SupportedLanguageMeta {
  key: string;
  code: string;
  short: string;
  label: string;
}

export const getSupportedLanguageMeta = (value: string): SupportedLanguageMeta | null => {
  const localeKey = normalizeLocaleKey(value);
  if (!localeKey) return null;

  const locale = LOCALE_BY_KEY.get(localeKey);
  if (!locale) return null;

  return {
    key: locale.key,
    code: locale.code,
    short: locale.code.toUpperCase(),
    label: locale.label,
  };
};

/**
 * Get language options for default language dropdown
 * Returns English, Korean, and Japanese
 */
export const getLanguageOptions = (): LanguageOption[] => {
  const defaultLanguageCodes = ['en', 'ko', 'ja'];
  return defaultLanguageCodes
    .map((code) => getSupportedLanguageMeta(code))
    .filter((item): item is SupportedLanguageMeta => item !== null)
    .map((item) => ({
      value: item.code,
      label: item.label,
    }));
};

/**
 * Get display name for a language code from supported languages API
 */
export const getSupportedLanguageName = (code: string): string => {
  return getSupportedLanguageMeta(code)?.label || code.toUpperCase();
};

/**
 * Get all supported language codes
 * Returns V1 canonical locale keys
 */
export const getAllLanguageCodes = (): string[] => {
  return V1_SUPPORTED_LOCALES.map((locale) => locale.key);
};
